import { useState } from 'react';
import { Hammer, Download, Home, Zap, Leaf, Users, TrendingUp } from 'lucide-react';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useRenovation } from '@/hooks/useRenovation';
import { isDataUnavailable } from '@/services/dataAvailability';
import { ProductionDataUnavailable } from '@/components/common/ProductionDataUnavailable';
import { scheduleBanner, dpe as dpeColors } from '@/styles/theme';
import { IndicatorCard } from '@/components/dashboard/IndicatorCard';
import { RenovationMap } from './RenovationMap';
import type { RenoLayer } from './RenovationMap';
import { RenovationSimulator } from './RenovationSimulator';
import type { SimResult } from './RenovationSimulator';

// ── Labels Loi Climat & Resilience ──────────────────────────────────────────

const DPE_LABELS: Record<'G' | 'F' | 'E', string> = {
  G: 'Interdiction location DPE G',
  F: 'Interdiction location DPE F',
  E: 'Interdiction location DPE E',
};

// ── RenovationEnergetique ────────────────────────────────────────────────────

/**
 * Renovation energetique — 9e ecran Terravision.
 * Aide a la decision : quels logements renover en priorite, avec quelles aides,
 * pour sortir le plus de menages de la precarite et tenir le calendrier Loi Climat.
 * Donnees issues de l'API (GET /territories/{insee}/renovation) via useRenovation.
 */
export function RenovationEnergetique() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const { data, isLoading, error, refetch } = useRenovation(insee);

  const [simResult, setSimResult] = useState<SimResult | null>(null);
  const [activeLayer, setActiveLayer] = useState<RenoLayer>('priority');
  const [showAfter, setShowAfter] = useState(false);

  // ── No territory selected ────────────────────────────────────────────────
  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Selectionnez un territoire pour acceder a la Renovation energetique.
        </p>
      </div>
    );
  }


  if (isDataUnavailable(error)) {
    return (
      <ProductionDataUnavailable
        feature="Simulation de rénovation"
        requiredSources="BDNB, ANAH, France Rénov' et INSEE Filosofi"
      />
    );
  }
  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div
          className="rounded-panel flex flex-col gap-2 px-5 py-4 text-sm font-medium"
          style={{ background: '#fdecec', color: '#e1000f' }}
          role="alert"
        >
          <span>Erreur lors du chargement des donnees de renovation.</span>
          <button
            type="button"
            className="self-start font-semibold underline"
            onClick={() => refetch()}
          >
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted animate-pulse">
          Chargement des donnees de renovation…
        </p>
      </div>
    );
  }

  // ── KPI values reactive to simulation ────────────────────────────────────
  const passoiresCount = simResult
    ? Math.max(0, data.passoires_count - simResult.renovated_units)
    : data.passoires_count;
  const precarityHouseholds = simResult
    ? Math.max(0, data.precarity_households - simResult.households_out_of_precarity)
    : data.precarity_households;
  const potentialGwh = simResult
    ? Math.max(0, data.potential_gwh - simResult.gwh_saved)
    : data.potential_gwh;
  const co2Avoided = simResult
    ? data.co2_avoided_t + simResult.co2_avoided
    : data.co2_avoided_t;

  return (
    <div className="flex flex-col gap-4 p-5">

      {/* ── Bandeau d'echeance Loi Climat & Resilience ───────────────────── */}
      <div
        className="flex items-center gap-4 rounded-panel px-5 py-3.5 flex-wrap"
        style={{ background: scheduleBanner }}
        role="region"
        aria-label="Echeancier Loi Climat et Resilience"
      >
        <Hammer size={22} aria-hidden="true" className="flex-shrink-0 text-white/80" />
        <span className="font-display text-[15px] font-extrabold text-white">
          Loi Climat &amp; Resilience — Echeancier des passoires
        </span>

        {/* 3 jalons : DPE lettre + date + libelle + logements concernes */}
        <div className="flex items-center gap-3 flex-wrap ml-2">
          {data.schedule.map((entry) => (
            <div
              key={entry.year}
              className="flex items-center gap-2 rounded-[9px] bg-white/10 px-3 py-1.5"
            >
              {/* Badge DPE : lettre + couleur (jamais couleur seule) */}
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-[5px] text-[13px] font-extrabold text-white"
                style={{ background: dpeColors[entry.dpe] }}
                aria-label={`DPE ${entry.dpe}`}
              >
                {entry.dpe}
              </span>
              <div>
                <div className="text-[13px] font-bold text-white leading-tight">{entry.year}</div>
                <div className="text-[10px] text-white/70">{entry.units.toLocaleString('fr-FR')} logts</div>
              </div>
              <div className="text-[10.5px] text-white/80 hidden xl:block">
                {DPE_LABELS[entry.dpe]}
              </div>
            </div>
          ))}
        </div>

        {/* Bouton export */}
        <button
          type="button"
          title="Bientôt disponible"
          className="ml-auto flex items-center gap-1.5 rounded-[9px] border border-white/30 bg-white/10 px-3 py-1.5 text-[12.5px] font-semibold text-white transition hover:bg-white/20"
          aria-label="Exporter le plan de campagne"
        >
          <Download size={14} aria-hidden="true" />
          Exporter le plan de campagne
        </button>
      </div>

      {/* ── Rangee KPI (5 cards) ─────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-3">
        <IndicatorCard
          label={`Passoires E/F/G (${data.passoires_pct} %)`}
          value={passoiresCount}
          unit="logements"
          icon={<Home size={15} />}
          color="#e30613"
          trend={simResult ? 'down' : 'stable'}
        />
        <IndicatorCard
          label="Menages precaires"
          value={precarityHouseholds}
          unit="menages"
          icon={<Users size={15} />}
          color="#ef7d18"
          trend={simResult ? 'down' : 'stable'}
        />
        <IndicatorCard
          label="Gain potentiel"
          value={potentialGwh}
          unit="GWh/an"
          icon={<Zap size={15} />}
          color="#1d4ed8"
          trend={simResult ? 'down' : 'stable'}
        />
        <IndicatorCard
          label="CO2 evite"
          value={co2Avoided}
          unit="t/an"
          icon={<Leaf size={15} />}
          color="#1f8a5b"
          trend={simResult ? 'up' : 'stable'}
        />
        <IndicatorCard
          label="Rythme renovation"
          value={data.pace_per_year}
          unit="logts/an"
          icon={<TrendingUp size={15} />}
          color="#1b2559"
        />
      </div>

      {/* ── Grille 1.55fr / 1fr : RenovationMap | RenovationSimulator ───── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1.55fr 1fr', minHeight: '520px' }}>
        <RenovationMap
          layer={activeLayer}
          onLayerChange={setActiveLayer}
          showAfter={showAfter}
          onShowAfterChange={setShowAfter}
          simDone={simResult !== null}
        />
        <RenovationSimulator onResult={setSimResult} />
      </div>

      {/* ── Bas : Secteurs prioritaires | Echeancier Loi Climat ──────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1.55fr 1fr' }}>

        {/* Secteurs prioritaires */}
        <div className="rounded-panel border border-line bg-white shadow-card overflow-hidden">
          <div className="border-b border-line px-4 py-3">
            <h3 className="font-display text-[14px] font-bold text-ink">Secteurs prioritaires</h3>
            <p className="text-[11.5px] text-faint">
              Classes par indice Passoires x Precarite x Gisement
            </p>
          </div>
          {data.sectors.length === 0 ? (
            <p className="p-4 text-sm text-muted">Aucun secteur a afficher.</p>
          ) : (
            <table className="w-full text-[12.5px]" aria-label="Secteurs prioritaires renovation">
              <thead className="bg-surface-subtle">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-faint">Rang</th>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-faint">Secteur</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Priorite</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Passoires</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Precarite</th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold text-faint">Gain GWh</th>
                </tr>
              </thead>
              <tbody>
                {data.sectors.map((s) => (
                  <tr key={s.rank} className="border-t border-line hover:bg-surface-subtle transition-colors">
                    <td className="px-4 py-2.5">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1b2559] font-mono text-[10.5px] font-bold text-white">
                        {s.rank}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-ink">{s.sector}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-ink">
                      {Math.round(s.priority * 100)}/100
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted">{s.passoires}</td>
                    <td className="px-4 py-2.5 text-right text-muted">
                      {Math.round(s.precarity * 100)}/100
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="font-mono font-semibold text-[#1f8a5b]">
                        {s.gain_gwh.toFixed(1)} GWh
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Echeancier Loi Climat & Resilience (3 jalons visuels) */}
        <div className="rounded-panel border border-line bg-white shadow-card overflow-hidden">
          <div className="border-b border-line px-4 py-3">
            <h3 className="font-display text-[14px] font-bold text-ink">
              Echeancier Loi Climat &amp; Resilience
            </h3>
            <p className="text-[11.5px] text-faint">Interdiction de location par classe DPE</p>
          </div>
          <div className="flex flex-col gap-4 p-4">
            {data.schedule.map((entry) => (
              <div key={entry.year} className="flex items-center gap-4">
                {/* Badge DPE : lettre + couleur */}
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[8px] text-[16px] font-extrabold text-white"
                  style={{ background: dpeColors[entry.dpe] }}
                  aria-label={`DPE ${entry.dpe}`}
                >
                  {entry.dpe}
                </div>
                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-[18px] font-extrabold text-ink leading-tight">
                      {entry.year}
                    </span>
                    <span className="text-[12px] font-semibold text-muted">
                      {DPE_LABELS[entry.dpe]}
                    </span>
                  </div>
                  <div className="text-[11.5px] text-faint">
                    {entry.units.toLocaleString('fr-FR')} logements concernes
                  </div>
                </div>
              </div>
            ))}
            <p className="mt-1 text-[10.5px] text-faint">
              Sources : BDNB (DPE batiment), INSEE Filosofi, MaPrimeRenov'/France Renov'/OPAH/CEE.
              Cadre : Loi Climat &amp; Resilience.
            </p>
          </div>
        </div>
      </div>

      {/* Sources de donnees */}
      <p className="pb-1 text-[10.5px] text-faint">
        Sources : BDNB (DPE au batiment), INSEE Filosofi (revenus/precarite), cadastre/BDTOPO,
        MaPrimeRenov'/France Renov'/OPAH/CEE.
        Cadre : Loi Climat &amp; Resilience (interdiction location G 2025 / F 2028 / E 2034).
      </p>
    </div>
  );
}
